import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

/**
 * Who asked to hear when the bottles ship.
 *
 * The site claimed a newsletter in its FAQ and had no signup anywhere, so
 * every visitor who liked the bottle but was not ready to spend $49.99 that
 * day left no trace. There is a signup now, in the footer and after the FAQ,
 * and this is where it lands.
 *
 * `source` is the whole point of the table: it says whether the footer or the
 * FAQ prompt is doing the work, which is the only way to decide where the next
 * one goes.
 */

interface Subscriber {
  email: string;
  source: string;
  status: 'subscribed' | 'unsubscribed';
  createdAt: string;
}

const PAGE_SIZE = 100;

const MailingList: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.getSubscribers({ limit: PAGE_SIZE });

      if (result.success) {
        const data = (result as { data?: { subscribers: Subscriber[] } }).data;
        setSubscribers(data?.subscribers ?? []);
      } else {
        setError(result.message || 'Could not load the mailing list');
      }
    } catch (err) {
      console.error('Mailing list failed to load:', err);
      setError('Could not reach the server for the mailing list');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = subscribers.filter((s) => s.status === 'subscribed');

  // Where they signed up, biggest first. This is the number that decides
  // whether the next prompt goes on the product page or nowhere.
  const bySource = active.reduce<Record<string, number>>((acc, s) => {
    const key = s.source || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const sources = Object.entries(bySource).sort((a, b) => b[1] - a[1]);

  /**
   * The list as a CSV, built in the browser.
   *
   * Whatever sends the mail — Klaviyo, Mailchimp, a person with a BCC field —
   * wants a file, and there is no integration to any of them. A download is
   * the shortest path from this table to an actual email.
   */
  const downloadCsv = () => {
    const rows = [
      ['email', 'source', 'status', 'signed_up'],
      ...subscribers.map((s) => [s.email, s.source || '', s.status, s.createdAt]),
    ];
    // Quoted and doubled, because an address or a source could contain a comma
    // or a quote and a half-escaped CSV is worse than none.
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `waddleandsip-list-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <p className="text-ink-muted">Loading the mailing list…</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface p-6">
        <p className="text-brand-red mb-4">{error}</p>
        <button type="button" onClick={load} className="btn-secondary">Try again</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-ink">Mailing list</h2>
          <p className="text-ink-muted">
            {active.length} subscribed
            {subscribers.length !== active.length
              ? `, ${subscribers.length - active.length} unsubscribed`
              : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} className="btn-secondary">Refresh</button>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={subscribers.length === 0}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Download CSV
          </button>
        </div>
      </div>

      {sources.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {sources.map(([source, count]) => (
            <div key={source} className="rounded-lg border border-border-subtle bg-surface px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-ink-muted">{source}</p>
              <p className="text-2xl font-semibold text-ink">{count}</p>
            </div>
          ))}
        </div>
      )}

      {subscribers.length === 0 ? (
        <div className="rounded-lg border border-border-subtle bg-surface p-8 text-center">
          <p className="text-ink-muted">
            Nobody has joined yet. The signup is in the site footer and under the FAQ.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border-subtle text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Signed up from</th>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((subscriber) => (
                <tr key={subscriber.email} className="border-b border-border-subtle last:border-0">
                  <td className="px-4 py-3 text-ink">{subscriber.email}</td>
                  <td className="px-4 py-3 text-ink-muted">{subscriber.source || '—'}</td>
                  <td className="px-4 py-3 text-ink-muted">
                    {new Date(subscriber.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {subscriber.status === 'subscribed' ? (
                      <span className="text-ink-muted">Subscribed</span>
                    ) : (
                      <span className="text-brand-red">Unsubscribed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MailingList;
